<script setup>
import { Server, Tag, Zap, CreditCard, Settings2, LogOut, MoreHorizontal, Bell } from 'lucide-vue-next';
import { AdminPortal, useFrontegg } from '@frontegg/vue';
import { useRouter, useRoute, RouterLink } from 'vue-router';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import AlertConfigurationModal from '@/components/AlertIntegrations/AlertConfigurationModal.vue';
import ThemeToggle from '@/components/ui/theme-toggle.vue';
import { ref, computed, onMounted } from 'vue';
import { useFronteggTheme } from '@/composables/useFronteggTheme';

const props = defineProps({
    user: { type: Object, required: true }
});

const { isMobile } = useSidebar();
const router = useRouter();
const route = useRoute();
const { initializeTheme } = useFronteggTheme();

const openAlertConfigurationModal = ref(false);

// Computed property to check active routes
const isActiveRoute = computed(() => {
    return (routeName) => {
        return route.name === routeName;
    };
});

const showAdminPortal = () => {
    AdminPortal.show();
};

const openAlertsSettings = () => {
    openAlertConfigurationModal.value = true;
};

const setAlertConfigurationModalOpenStatus = (value) => {
    openAlertConfigurationModal.value = value;
};

// Initialize Frontegg theme synchronization
onMounted(() => {
    initializeTheme();
});
</script>

<template>
    <SidebarMenu>
        <SidebarMenuItem>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <SidebarMenuButton size="lg" class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                        <Avatar class="h-8 w-8 rounded-lg">
                            <AvatarImage :src="user.profilePictureUrl" :alt="user.name" />
                            <AvatarFallback class="rounded-lg"> CN </AvatarFallback>
                        </Avatar>
                        <div class="grid flex-1 text-left text-sm leading-tight">
                            <span class="truncate font-medium">{{ user.name }}</span>
                            <span class="truncate text-xs">{{ user.email }}</span>
                        </div>
                        <MoreHorizontal class="ml-auto size-4" />
                    </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    :side="isMobile ? 'bottom' : 'right'"
                    align="end"
                    :side-offset="4">
                    <DropdownMenuLabel class="p-0 font-normal">
                        <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                            <Avatar class="h-8 w-8 rounded-lg">
                                <AvatarImage :src="user.profilePictureUrl" :alt="user.name" />
                                <AvatarFallback class="rounded-lg"> ST </AvatarFallback>
                            </Avatar>
                            <div class="grid flex-1 text-left text-sm leading-tight">
                                <span class="truncate font-semibold">{{ user.name }}</span>
                                <span class="truncate text-xs">{{ user.email }}</span>
                            </div>
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuItem class="p-2" @click.stop>
                            <ThemeToggle />
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuItem as-child v-can-access.permissions.or="[$Permissions.READ_SERVICES]">
                            <RouterLink
                                :to="{ name: 'services' }"
                                :class="['flex items-center', { 'bg-accent text-accent-foreground': isActiveRoute('services') }]">
                                <Server class="mr-2 size-4" />
                                Services
                            </RouterLink>
                        </DropdownMenuItem>
                        <DropdownMenuItem as-child v-can-access.permissions.or="[$Permissions.READ_TAGS]">
                            <RouterLink
                                :to="{ name: 'tags' }"
                                :class="['flex items-center', { 'bg-accent text-accent-foreground': isActiveRoute('tags') }]">
                                <Tag class="mr-2 size-4" />
                                Tags
                            </RouterLink>
                        </DropdownMenuItem>
                        <DropdownMenuItem as-child v-can-access.permissions.or="[$Permissions.READ_KAFKA_ACCESS]">
                            <RouterLink
                                :to="{ name: 'kafka-access' }"
                                :class="['flex items-center', { 'bg-accent text-accent-foreground': isActiveRoute('kafka-access') }]">
                                <Zap class="mr-2 size-4" />
                                Kafka Access
                            </RouterLink>
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="openAlertsSettings">
                            <Bell class="mr-2 size-4" />
                            Alerts Settings
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuItem
                            as-child
                            v-can-access.permissions.and="[
                                $Permissions.READ_BILLING_METRICS,
                                $Permissions.READ_ORGANISATION,
                                $Permissions.READ_SOURCE_METRICS,
                                $Permissions.READ_DESTINATION_METRICS
                            ]">
                            <RouterLink
                                :to="{ name: 'billing' }"
                                :class="['flex items-center', { 'bg-accent text-accent-foreground': isActiveRoute('billing') }]">
                                <CreditCard class="mr-2 size-4" />
                                Billing
                            </RouterLink>
                        </DropdownMenuItem>
                        <DropdownMenuItem @click="showAdminPortal()">
                            <Settings2 class="mr-2 size-4" />
                            Settings
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem @click="router.push('/account/logout')">
                        <LogOut class="mr-2 size-4" />
                        Log out
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </SidebarMenuItem>
    </SidebarMenu>

    <!-- Alert Configuration Modal -->
    <AlertConfigurationModal
        v-show="openAlertConfigurationModal"
        :is-modal-open="openAlertConfigurationModal"
        @update:is-open="setAlertConfigurationModalOpenStatus($event)" />
</template>
